/**
 * Public marketing/customer path allowlist for cookieless analytics.
 *
 * keep in sync with server paths.py
 * Dual-gate companion to products/cookieless_analytics/paths.py
 */

export const PUBLIC_ANALYTICS_EXACT = new Set([
  '/',
  '/index.html',
  '/about.html',
  '/help.html',
  '/terms.html',
  '/privacy.html',
  '/cookie-preferences.html',
  '/accessibility',
  '/accessibility.html',
  '/categories.html',
  '/search.html',
  '/product.html',
  '/trusted-finder.html',
  '/newsletter-confirm.html',
]);

// '/p/' is the canonical product detail page (/p/<slug>-<id>/). It was missing
// here after the slug-URL migration, so product pages recorded nothing while the
// legacy '/product.html' entry above kept matching a URL that now only 301s.
export const PUBLIC_ANALYTICS_PREFIXES = ['/blog/', '/docs/', '/compare/', '/p/'];

/** Paths that must never retain query strings (stored path = pathname only). */
export const STRIP_QUERY_PATHS = new Set([
  '/search.html',
  '/trusted-finder.html',
]);

// Matched against the *first path segment*, not as substrings. These used to be
// substring checks, so a blog post at /blog/how-to-close-your-ebay-account/ was
// silently dropped for containing 'account'.
const DENY_FIRST_SEGMENTS = new Set([
  'admin',
  'admin.html',
  'admin-login.html',
  'admin-dashboard.html',
  'admin-submissions.html',
  'account',
  'account.html',
  'login',
  'login.html',
  'register',
  'register.html',
  'reset-password',
  'reset-password.html',
  'accept-admin-invite',
  'accept-admin-invite.html',
  'analytics',
  'analytics.html',
]);

/** Characters a percent-encoded URL path can never legitimately contain. */
const UNSAFE_PATH_CHARS = /[<>"'`\\{}|^\s]|[\u0000-\u001f\u007f]/;

const MAX_PATH_LEN = 500;

/**
 * @param {string} raw
 * @returns {{ pathname: string, query: string }}
 */
export function splitPathAndQuery(raw) {
  if (!raw) {
    return { pathname: '/', query: '' };
  }
  const text = String(raw).split('#', 1)[0].trim();
  if (!text) {
    return { pathname: '/', query: '' };
  }

  let pathname;
  let query = '';

  if (text.includes('://')) {
    try {
      const u = new URL(text);
      pathname = u.pathname || '/';
      query = u.search ? u.search.slice(1) : '';
    } catch {
      pathname = '/';
      query = '';
    }
  } else {
    const withSlash = text.startsWith('/') ? text : `/${text.replace(/^\/+/, '')}`;
    const qIdx = withSlash.indexOf('?');
    if (qIdx === -1) {
      pathname = withSlash;
    } else {
      pathname = withSlash.slice(0, qIdx) || '/';
      query = withSlash.slice(qIdx + 1);
    }
  }

  if (!pathname.startsWith('/')) {
    pathname = `/${pathname}`;
  }
  return { pathname, query };
}

/**
 * @param {string} pathname
 * @returns {string}
 */
export function normalizePathname(pathname) {
  if (!pathname) {
    return '/';
  }
  if (pathname !== '/' && pathname.endsWith('/')) {
    pathname = pathname.replace(/\/+$/, '') || '/';
  }
  return pathname;
}

/**
 * @param {string} pathname
 * @returns {Set<string>}
 */
function membershipCandidates(pathname) {
  const base = normalizePathname(pathname);
  const candidates = new Set([base, pathname]);
  if (base === '/') {
    candidates.add('/index.html');
  }
  if (base === '/index.html') {
    candidates.add('/');
  }
  if (base.endsWith('.html')) {
    candidates.add(base.slice(0, -5) || '/');
  } else if (base !== '/') {
    candidates.add(`${base}.html`);
  }
  if (base !== '/' && !base.endsWith('/')) {
    candidates.add(`${base}/`);
  }
  return new Set([...candidates].map((c) => normalizePathname(c)));
}

/**
 * @param {string} pathname
 * @param {string} raw
 * @returns {boolean}
 */
function isDenied(pathname, raw) {
  if (UNSAFE_PATH_CHARS.test(String(raw || '')) || UNSAFE_PATH_CHARS.test(pathname)) {
    return true;
  }
  const firstSegment = pathname.replace(/^\/+|\/+$/g, '').split('/')[0].toLowerCase();
  return DENY_FIRST_SEGMENTS.has(firstSegment);
}

/**
 * Strip hash and query; return pathname normalized for allowlist membership.
 * @param {string} path
 * @returns {string}
 */
export function normalizePathForAllowlist(path) {
  const { pathname } = splitPathAndQuery(path || '');
  return normalizePathname(pathname);
}

/**
 * Path string to send in beacon body (parity with server normalize_stored_paths first element).
 * Search/TF: pathname only; others may keep query (capped).
 * @param {string} path
 * @returns {string}
 */
export function normalizePathForBeacon(path) {
  let { pathname, query } = splitPathAndQuery(path || '');
  pathname = normalizePathname(pathname);

  for (const candidate of membershipCandidates(pathname)) {
    if (PUBLIC_ANALYTICS_EXACT.has(candidate)) {
      pathname = candidate;
      break;
    }
  }

  if (STRIP_QUERY_PATHS.has(pathname)) {
    return pathname.slice(0, MAX_PATH_LEN);
  }

  if (query) {
    return `${pathname}?${query}`.slice(0, MAX_PATH_LEN);
  }
  return pathname.slice(0, MAX_PATH_LEN);
}

/**
 * True if path is an allowlisted public marketing/customer page.
 * @param {string} path
 * @returns {boolean}
 */
export function isPublicAnalyticsPath(path) {
  if (path == null) {
    return false;
  }
  const pathname = normalizePathForAllowlist(path);
  if (isDenied(pathname, path)) {
    return false;
  }

  const candidates = membershipCandidates(pathname);
  for (const c of candidates) {
    if (PUBLIC_ANALYTICS_EXACT.has(c)) {
      return true;
    }
  }

  for (const prefix of PUBLIC_ANALYTICS_PREFIXES) {
    const check = pathname.endsWith('/') ? pathname : `${pathname}/`;
    if (check.startsWith(prefix) || pathname.startsWith(prefix)) {
      return true;
    }
    const bare = prefix.replace(/\/$/, '');
    if (pathname === bare) {
      return true;
    }
  }

  return false;
}
